import { GridColumn } from '@entity-framework/entity-grid/state/entity-grid-column';
import { columnFormatters } from '@utils/display-formatters';
import { CellClassParams } from '@ag-grid-community/core';
import { ContributionRecordError } from './state/pension-contribution-employee.model';

const hasErrors = (params: CellClassParams): boolean => {
  return params.data?.errors && params.data.errors.length > 0;
};

const getRowErrorClassRules = () => ({
  'error-row-cell': (params: CellClassParams) => hasErrors(params)
});

export const pensionContributionsGridColumns = (enableRowGroup = false, showEmployerRefPaymentSourceColumn = true): GridColumn[] => {
  const employerRefPaymentSourceColumn: GridColumn = {
    propertyName: 'employerRefPaymentSource',
    headerLabel: 'Employer ref. / Payment source',
    propertyType: 'text',
    sortable: true,
    formatForDisplay: (value: string) => value || '-',
    colDefOverrides: {
      flex: 1.5,
      minWidth: 180,
      cellClassRules: getRowErrorClassRules(),
      suppressColumnsToolPanel: true,
      hide: true,
      ...(enableRowGroup && { rowGroup: true })
    }
  };

  const restColumns: GridColumn[] = [
    {
      propertyName: 'employmentId',
      headerLabel: 'Employment id',
      propertyType: 'text',
      sortable: true,
      colDefOverrides: {
        flex: 1,
        minWidth: 120,
        cellClassRules: getRowErrorClassRules()
      }
    },
    {
      propertyName: 'surname',
      headerLabel: 'Surname',
      propertyType: 'text',
      sortable: true,
      colDefOverrides: {
        flex: 1,
        minWidth: 120,
        cellClassRules: getRowErrorClassRules()
      }
    },
    {
      propertyName: 'firstName',
      headerLabel: 'First name',
      propertyType: 'text',
      sortable: true,
      colDefOverrides: {
        flex: 1,
        minWidth: 120,
        cellClassRules: getRowErrorClassRules()
      }
    },
    {
      propertyName: 'errors',
      headerLabel: 'Error description',
      propertyType: 'text',
      sortable: false,
      formatForDisplay: (value: ContributionRecordError[]) =>
        value && value.length > 0 ? value.map((e) => `• ${e.errorDescription}`).join('\n') : '',
      colDefOverrides: {
        flex: 3,
        minWidth: 200,
        wrapText: true,
        autoHeight: true,
        cellClassRules: {
          'error-row-cell': (params: CellClassParams) => hasErrors(params),
          'error-text': (params: CellClassParams) => hasErrors(params)
        },
        cellStyle: { whiteSpace: 'pre-wrap', lineHeight: '1.4' }
      }
    },
    {
      propertyName: 'periodEarnings',
      headerLabel: 'Period earnings',
      propertyType: 'number',
      sortable: true,
      alignRight: true,
      formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
      colDefOverrides: {
        flex: 1.5,
        minWidth: 130,
        type: 'numericColumn',
        cellClassRules: getRowErrorClassRules(),
        ...(enableRowGroup && { aggFunc: 'sum' })
      }
    },
    {
      propertyName: 'employeeContribution',
      headerLabel: 'Employee contribution',
      propertyType: 'number',
      sortable: true,
      alignRight: true,
      formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
      colDefOverrides: {
        flex: 1.8,
        minWidth: 150,
        type: 'numericColumn',
        cellClassRules: getRowErrorClassRules(),
        ...(enableRowGroup && { aggFunc: 'sum' })
      }
    },
    {
      propertyName: 'employerContribution',
      headerLabel: 'Employer contribution',
      propertyType: 'number',
      sortable: true,
      alignRight: true,
      formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
      colDefOverrides: {
        flex: 1.8,
        minWidth: 150,
        type: 'numericColumn',
        cellClassRules: getRowErrorClassRules(),
        ...(enableRowGroup && { aggFunc: 'sum' })
      }
    },
    {
      propertyName: 'total',
      headerLabel: 'Total',
      propertyType: 'number',
      sortable: true,
      alignRight: true,
      formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
      textBold: () => true,
      colDefOverrides: {
        flex: 1.2,
        minWidth: 110,
        type: 'numericColumn',
        cellClassRules: getRowErrorClassRules(),
        ...(enableRowGroup && { aggFunc: 'sum' })
      }
    },
    {
      propertyName: 'pensionGroup',
      headerLabel: 'Pension group',
      propertyType: 'text',
      sortable: true,
      formatForDisplay: (value: string | null) => value || '-',
      colDefOverrides: {
        flex: 1.5,
        minWidth: 130,
        cellClassRules: getRowErrorClassRules()
      }
    }
  ];

  return showEmployerRefPaymentSourceColumn ? [employerRefPaymentSourceColumn, ...restColumns] : restColumns;
};
